import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { CheckSquare } from '@phosphor-icons/react/dist/ssr/CheckSquare';
import { Square } from '@phosphor-icons/react/dist/ssr/Square';
import { ButtonRounded } from '../../../UISystem/Buttons';
import { SHADOWS } from '../../../../styles/UISystem';
import { buildFollowUpDraft } from '../../../../services/sharing/followUpText';
import { ALL_CONTACT_TYPES } from '../../../../utils/contactTypeConfig';
import { getProfileData } from '../../../../services/storage/userDataStorage';
import { buildPermanentUrl } from '../../../../utils/permanentUrl';
import { firstNameOf } from '../../../../utils/contactUtils';

/**
 * SendMessageOverlay — the TEXT variant's send surface (plan 2, epic §9):
 * an editable draft auto-written from moment data (buildFollowUpDraft — the
 * gated place + at/in rails), an "Include link to card" checkbox DEFAULT
 * UNCHECKED (host-judgment consent: the host read the room; unchecked keeps
 * the link "on request" in spirit).
 *
 * Two send shapes by channel (user design, device pass 1j):
 * - phone (and WhatsApp — wa.me carries ?text= natively): [Send] opens the
 *   host's own composer, prefilled and addressed (V1 composer path, real
 *   message from their number, zero backend)
 * - other non-phone (Instagram/email/...): no composer prefill exists, so
 *   the guided two-step in one slot — [Copy text] copies the draft, then
 *   the button becomes [Open {channel}]; the host pastes into the DM.
 *   Editing after copying re-arms the copy step (never a stale copy).
 *
 * The link APPENDS at the end of the draft, never leads. onSend receives
 * ({ body, linkIncluded }) — the caller owns the launch (sms: or channel
 * link), the analytics stamp, and the one-shot retire.
 */
const SendMessageOverlay = ({
  anonName,
  connectionMetadata,
  channelType = 'phone',
  onSend,
  onClose,
}) => {
  const [draft, setDraft] = useState('');
  const [includeLink, setIncludeLink] = useState(false);
  const [copied, setCopied] = useState(false);
  // One fact drives the shape (/simplify: label + directSend + label-absence
  // sentinel all encoded the channel type)
  const isPhone = channelType === 'phone';
  const oneTap = isPhone || channelType === 'whatsapp';
  const channelLabel = ALL_CONTACT_TYPES[channelType]?.label;

  // Body scroll lock (match QuickAddOverlay)
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Prefill once the host's own name resolves (localStorage-backed — fast)
  useEffect(() => {
    let cancelled = false;
    getProfileData().then((profile) => {
      if (cancelled) return;
      setDraft(
        buildFollowUpDraft({
          anonName,
          hostName: profile?.name || '',
          connectionMetadata,
        })
      );
    });
    return () => {
      cancelled = true;
    };
    // Prefill is a mount-time computation from stable moment data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildBody = () => {
    const link = buildPermanentUrl(localStorage.getItem('profile_user_id'));
    return includeLink ? `${draft}\n${link}` : draft;
  };

  const handleSend = () => {
    onSend({ body: buildBody(), linkIncluded: includeLink });
  };

  // Copy advances only on a REAL write — a rejected clipboard call leaves
  // the button on [Copy text] for a retry (no fake success)
  const handleCopy = async () => {
    await navigator.clipboard.writeText(buildBody());
    setCopied(true);
  };

  const first = firstNameOf(anonName);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        background: 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
    >
      <motion.div
        initial={{ y: 400 }}
        animate={{ y: 0 }}
        exit={{ y: 400 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          backgroundColor: 'white',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          boxShadow: SHADOWS.TIER3,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '28px 24px',
            paddingBottom: 'calc(28px + env(safe-area-inset-bottom))',
            maxWidth: '400px',
            margin: '0 auto',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#000000',
              textAlign: 'center',
            }}
          >
            {first ? `${isPhone ? 'Text' : 'Message'} ${first}` : 'Send a message'}
          </div>

          <textarea
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setCopied(false);
            }}
            rows={3}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '12px 14px',
              borderRadius: '16px',
              border: '1px solid #E3E3E7',
              fontSize: '16px',
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
              color: '#000000',
              resize: 'none',
              outline: 'none',
            }}
          />

          {/* Host-judgment consent — default OFF; the link appends, never leads */}
          <div
            onClick={() => {
              setIncludeLink((v) => !v);
              setCopied(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '15px',
              color: '#6A6F7A',
              cursor: 'pointer',
              alignSelf: 'center',
            }}
          >
            {includeLink ? (
              <CheckSquare size={20} weight="fill" color="#000000" />
            ) : (
              <Square size={20} />
            )}
            Include link to card
          </div>

          {copied && (
            <div
              style={{
                fontSize: '13px',
                color: '#8E8E93',
                textAlign: 'center',
                marginTop: '-8px',
              }}
            >
              Copied — paste it into your message
            </div>
          )}

          <ButtonRounded
            text={oneTap ? 'Send' : copied ? `Open ${channelLabel}` : 'Copy text'}
            variant="primary"
            size="full"
            disabled={!draft.trim()}
            onClick={oneTap || copied ? handleSend : handleCopy}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

SendMessageOverlay.propTypes = {
  anonName: PropTypes.string,
  connectionMetadata: PropTypes.object,
  // Contact type the message goes to ('phone' default; 'whatsapp' also
  // one-tap; others get the copy-then-open flow)
  channelType: PropTypes.string,
  onSend: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SendMessageOverlay;
